import type { Workspace } from '@/api/skillhub/types';
import { defineStore } from 'pinia';
import { getMySessionWorkspace } from '@/api/skillhub';

/**
 * Project-workspace panel state for the AI 助手 page.
 *
 * The current conversation (its OpenClaw session key) lives inside the chat
 * layout component while the panel renders as a sibling in the host page, so
 * this store is the shared bridge: the chat layout publishes the active
 * session key here, and both sides read the resolved binding. Not persisted —
 * visibility resets per page load and the binding is refetched from the
 * Controller anyway.
 */
export const useWorkspaceStore = defineStore('workspacePanel', () => {
  /** Whether the workspace panel is expanded on the assistant page. */
  const panelVisible = ref(false);

  /** The active assistant conversation's OpenClaw session key. */
  const sessionKey = ref('');

  /** The workspace bound to that conversation (null when unbound/unknown). */
  const boundWorkspace = ref<Workspace | null>(null);

  /**
   * Workspace chosen when starting a new chat from a sidebar workspace group.
   * The binding cannot be persisted yet (no session key exists until the first
   * turn), so it rides along for the working-directory hint and is persisted
   * by the chat layout once the session is adopted. Cleared by any action that
   * starts/opens a conversation without workspace intent.
   */
  const pendingBindWorkspace = ref<Workspace | null>(null);

  function setPanelVisible(visible: boolean) {
    panelVisible.value = visible;
  }

  function togglePanel() {
    panelVisible.value = !panelVisible.value;
  }

  /**
   * Publish the active conversation and (re)load its workspace binding.
   * Called by the chat layout whenever the session key changes. Binding
   * errors leave the previous value cleared — an unbound panel is the safe
   * default.
   */
  async function setSession(key: string) {
    sessionKey.value = key;
    boundWorkspace.value = null;
    if (!key)
      return;
    try {
      boundWorkspace.value = await getMySessionWorkspace(key);
    }
    catch {
      boundWorkspace.value = null;
    }
  }

  /** Adopt a locally changed binding (after bind/unbind in the panel). */
  function applyBinding(workspace: Workspace | null) {
    boundWorkspace.value = workspace;
  }

  /** Arm a pending binding for a new chat started inside a workspace group. */
  function startPendingBind(workspace: Workspace) {
    pendingBindWorkspace.value = workspace;
  }

  /** Drop any armed pending binding (plain new chat / opening a conversation). */
  function clearPendingBind() {
    pendingBindWorkspace.value = null;
  }

  /**
   * The workspace the panel should display: the real binding, falling back to
   * the armed pending binding while its new chat has no session key yet. The
   * pending binding only survives on the new-chat route (opening/starting a
   * conversation without workspace intent clears it), so the fallback never
   * masks a genuinely unbound existing conversation.
   */
  const effectiveWorkspace = computed(() => boundWorkspace.value ?? pendingBindWorkspace.value);

  return {
    panelVisible,
    sessionKey,
    boundWorkspace,
    pendingBindWorkspace,
    effectiveWorkspace,
    setPanelVisible,
    togglePanel,
    setSession,
    applyBinding,
    startPendingBind,
    clearPendingBind,
  };
});
